// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useTranslation } from 'react-i18next';
import styles from './ShipmentRegistration.module.scss';
import { useProcessingColumns } from './useProcessingColumns';
import { useStorageColumns } from './useStorageColumns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CompressionEditModal } from 'components/organisms/CompressionEditModal';
import {
  Compression,
  CompressionDeliveryQRSScannedData,
  Option,
  ShipmentRegistrationFilter,
  StorageProcessingObject,
  ShipmentRegistrationRow,
  StorageShipmentRow,
} from 'util/Types';
import { scanStorageSiteQr } from 'apis/site_storage';
import { StorageSiteScanQrDataType } from 'types/siteStorage';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import TableFilterMultiSelect from 'components/molecules/TableFilterMultiSelect';
import ResponsiveHistoryTable from 'components/molecules/ResponsiveHistoryTable';
import { createAxios } from 'ts/createAxios';
import { API_ENDPOINTS } from 'util/endPoints';
import { handleError, isError } from 'util/errorHandler';
import { getApiPath, normalizeNumber } from 'util/commons';
import {
  useCreateCompressionDeliveriesMutation,
  useFetchNextStageSitesMutation,
} from 'apis/compressions_user/compressionUserApi';
import ExpectedDestinationModal from 'components/molecules/ExpectedDestinationModal';
import ErrorModal from 'components/molecules/ErrorModal';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import QRScanningModal from '../QRScanningModal';
import { selectLoading } from 'redux/slices/loadingSlice';
import { QRScanningProps } from '../QRScanning';
import { IconCheck } from 'components/atoms/icons/IconCheck';
import { IconEyeSlash } from 'components/atoms/icons/IconEyeSlash';
import Popover from 'components/atoms/Popover';
import Portal from 'components/atoms/Portal';
import { CompressionSiteType } from 'util/Enums';
import {
  getFormatDateWithCurrentTimezone,
  getFormatDateWithDeviceTimezone,
} from 'ts/formatDate';
import { DATE_FORMAT } from 'util/ConstantValues';
import BigNumber from 'bignumber.js';

export type Props = {
  id?: string;
};

type ScanGroupItem = {
  id: string;
  materialName: string;
  weight: string;
  grade: string;
  kind: string;
};

const ShipmentRegistration = ({ id }: Props) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [fnNextStage] = useFetchNextStageSitesMutation();
  const [fnCreateBookingDeliveries] = useCreateCompressionDeliveriesMutation();
  const { isLoading } = useSelector(selectLoading);

  const isStorage = user?.siteType === CompressionSiteType.Storage;

  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [checkedQRIds, setCheckedQRIds] = useState<string[]>([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<ShipmentRegistrationFilter>({});
  const [originalCompressions, setOriginalCompressions] =
    useState<Compression[]>();
  const [compressions, setCompressions] = useState<Compression[]>([]);
  const [QRSelectedCompressions, setQRSelectedCompressions] = useState<
    Compression[]
  >([]);

  const [originalStorageMaterial, setOriginalStorageMaterial] =
    useState<StorageProcessingObject[]>();

  const [storageProcessingObjects, setStorageProcessingObjects] = useState<
    StorageProcessingObject[]
  >([]);

  const [isRefresh, setRefresh] = useState(false);
  const [modalError, setModalError] = useState<boolean>(false);
  const [modalExpected, setModalExpected] = useState<
    | boolean
    | {
        compressionIds: string[];
      }
  >(false);
  const [modalNoData, setModalNoData] = useState<boolean>(false);
  const [modalScanQr, setModalScanQr] = useState<boolean>(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [scannedData, setScannedData] = useState<
    Record<
      string,
      CompressionDeliveryQRSScannedData | StorageSiteScanQrDataType
    >
  >({});

  const qrOnSuccessRef = useRef<(() => void) | null>(null);
  const qrRemainingRef = useRef<number>(0);
  const isFromQRRef = useRef<boolean>(false);

  const handleScan: QRScanningProps<
    CompressionDeliveryQRSScannedData,
    ScanGroupItem
  >['onScan'] = async (qrCode, addItem) => {
    if (!qrCode || isLoading || scannedData[qrCode]) {
      return;
    }

    try {
      if (isStorage) {
        await scanStorageSiteQr({
          qrCode,
          successCallback: (data) => {
            setScannedData((prev) => ({
              ...prev,
              [qrCode]: data,
            }));
            addItem(data as unknown as CompressionDeliveryQRSScannedData);
            setCheckedQRIds((prev) => [...prev, data.id]);
          },
        });
      } else {
        const { data } = await createAxios({
          prefix: 'v2',
        }).get<CompressionDeliveryQRSScannedData>(
          getApiPath(API_ENDPOINTS.COMPRESSION_DETAIL, {
            id: qrCode,
          })
        );

        setScannedData((prev) => ({
          ...prev,
          [qrCode]: {
            ...data,
            qrCode,
          },
        }));
        addItem(data);
        setCheckedQRIds((prev) => [...prev, data.id]);
        handleSetQRSelectedCompressions(data);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleClearWhenCloseQRModal = () => {
    setModalScanQr(false);
    setCheckedQRIds([]);
    setQRSelectedCompressions([]);
    setScannedData({});
  };

  const handleSetQRSelectedCompressions = (
    data: CompressionDeliveryQRSScannedData
  ) => {
    const formattedData: Compression = {
      compressionId: data.id,
      datetime: getFormatDateWithCurrentTimezone(
        data.registDatetime,
        DATE_FORMAT.slaYMDHm
      ),
      userId: '',
      userName: '',
      weight: data.weight.replace(/[^0-9.]/g, ''),
      description: data.materialName,
      grade: {
        id: data?.grade?.id ?? 0,
        name: data.grade?.name ?? '',
      },
      gradeId: data.grade?.id ?? 0,
      siteId: id ?? '',
      packingStyle: data.type ?? '',
      materialName: data.materialName,
      totalInputWeight: (data.totalInputWeight ?? data.weight).replace(
        /[^0-9.]/g,
        ''
      ),
    };

    setQRSelectedCompressions((prev) => [...prev, formattedData]);
  };

  const fnGetNextStage = async (
    siteId: string | null,
    compressionsIds: string[]
  ) => {
    return await fnNextStage({
      siteId,
      ...(isStorage
        ? { processingObjectIds: compressionsIds }
        : { compressionsIds }),
    });
  };

  const callGetCompressionsApi = async () => {
    try {
      const res = await createAxios().get(API_ENDPOINTS.COMPRESSIONS, {
        params: {
          descriptions: filter.materialName ?? [],
          gradeIds: filter.grade ?? [],
          types: filter.kind ?? [],
          dateFrom: filter.datetime?.[0].replace(/-/g, '/') ?? '',
          dateTo: filter.datetime?.[1]?.replace(/-/g, '/') ?? '',
        },
      });

      if (!originalCompressions) {
        setOriginalCompressions(res.data);
      }
      setCompressions(res.data);
      setCheckedIds([]);
      setIsEditModalOpen(false);

      if (!compressions.length) {
        setCompressions(res.data);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const callGetProcessingObjectsApi = async () => {
    try {
      const res = await createAxios().get(
        API_ENDPOINTS.COMPRESSIONS_PROCESSING_OBJECTS,
        {
          params: {
            dateFrom: filter.datetime?.[0].replace(/-/g, '/') ?? '',
            dateTo: filter.datetime?.[1]?.replace(/-/g, '/') ?? '',
            materialNames: filter.materialName ?? [],
          },
        }
      );

      const data = res.data?.data ?? [];
      if (!originalStorageMaterial) {
        setOriginalStorageMaterial(data);
      }
      setStorageProcessingObjects(data);

      setCheckedIds([]);
    } catch (error) {
      handleError(error);
    }
  };

  const submit = async (
    compressions: { id: string; description: string }[],
    fromQRScanning?: boolean
  ) => {
    try {
      const filter = compressions.filter(
        (x) => x?.description === compressions[0]?.description
      );

      if (filter.length !== compressions.length) {
        setModalError(true);
      } else {
        const compressionIds = compressions.map(({ id }) => id);

        fnGetNextStage(user.siteId, compressionIds)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .then((rs: any) => {
            if (isError(rs)) {
              return;
            }

            if (rs?.data?.length > 0) {
              setOptions(rs.data);

              if (fromQRScanning) {
                setModalExpected({
                  compressionIds,
                });
                // Don't close QR modal – items are removed after delivery API succeeds
              } else {
                setModalExpected(true);
              }
            } else {
              setModalNoData(true);
            }
          });
      }
    } catch (e) {
      toast.error(t('receives.signup_messages.failed'));
      setIsEditModalOpen(false);
    }
  };

  const handleSubmit = async (stage: string, compressionIds?: string[]) => {
    try {
      setModalExpected(false);

      const ids =
        compressionIds ||
        Object.keys(checkedItems).filter((compressionsId) => {
          return checkedItems[compressionsId as keyof typeof checkedItems];
        });

      const data = {
        ...(isStorage
          ? { processingObjectIds: ids }
          : { compressionsIds: ids }),
        temporaryTargetSiteId: stage,
      };

      const result = await fnCreateBookingDeliveries(data);

      if (isError(result)) return;
      toast.success(t('messages.M_129'));

      setIsEditModalOpen(false);
      setCheckedIds([]);
      setCheckedItems({});
      setCompressions([]);
      setRefresh(!isRefresh);

      if (isFromQRRef.current) {
        qrOnSuccessRef.current?.();
        if (qrRemainingRef.current === 0) {
          handleClearWhenCloseQRModal();
        }
        isFromQRRef.current = false;
        qrOnSuccessRef.current = null;
      }
    } catch (e) {
      toast.error(t('receives.signup_messages.failed'));
    }
  };

  const convertedCompressions = useMemo(
    () =>
      compressions.map(
        ({
          datetime,
          userId,
          userName,
          weight,
          type,
          compressionId,
          grade,
          description,
          totalInputWeight,
        }) => ({
          id: compressionId || '',
          processingDatetime: getFormatDateWithDeviceTimezone(datetime),
          personInCharge: `${userName} (${userId || ''})`,
          weight: normalizeNumber({
            value: weight,
          }),
          totalInputWeight: normalizeNumber({ value: totalInputWeight }),
          kind: type || '',
          grade: grade?.name || '',
          gradeId: grade?.id || 0,
          materialName: description || '', // materialName is not provided in the API response, so set it as an empty string for now
        })
      ),
    [compressions]
  );

  const { materialNameOptions, gradeOptions, typeOptions } = useMemo(() => {
    const materialNames = new Set(
      originalCompressions?.map((item) => item.description)
    );
    const gradeIds = new Set(
      originalCompressions?.map((item) => item.grade?.id).filter(Boolean)
    );
    const types = new Set(
      originalCompressions?.map((item) => item.type || '').filter(Boolean)
    );

    return {
      materialNameOptions: Array.from(materialNames).map((name) => ({
        value: name,
        label: name,
      })),
      gradeOptions: Array.from(gradeIds).map((id) => {
        const grade = originalCompressions?.find(
          (item) => item.grade?.id === id
        )?.grade;

        return {
          value: id.toString(),
          label: grade?.name || '',
        };
      }),
      typeOptions: Array.from(types).map((type) => ({
        value: type,
        label: type,
      })),
    };
  }, [originalCompressions]);

  useEffect(() => {
    if (isStorage) {
      callGetProcessingObjectsApi();
    } else {
      callGetCompressionsApi();
    }
  }, [isRefresh, filter, isStorage]);

  const selectedCompressions: Compression[] = useMemo(
    () =>
      modalScanQr
        ? QRSelectedCompressions
        : convertedCompressions
            .filter((row) => checkedIds.includes(row.id))
            .map((row) => ({
              compressionId: row.id,
              datetime: row.processingDatetime,
              userId: '',
              userName: row.personInCharge,
              weight: row.weight.replace(/[^0-9.]/g, ''),
              description: row.materialName,
              grade: { id: row.gradeId, name: row.grade },
              gradeId: row.gradeId,
              siteId: id ?? '',
              packingStyle: row.kind,
              materialName: row.materialName,
              totalInputWeight: row.totalInputWeight.replace(/[^0-9.]/g, ''),
            })),
    [convertedCompressions, checkedIds, modalScanQr, QRSelectedCompressions]
  );

  const selectedMaterialName = useMemo(() => {
    const checkedRow = convertedCompressions.find((row) =>
      checkedIds.includes(row.id)
    );
    return checkedRow?.materialName ?? null;
  }, [checkedIds, convertedCompressions]);

  const selectableCompressions = useMemo(
    () =>
      selectedMaterialName
        ? convertedCompressions.filter(
            (row) => row.materialName === selectedMaterialName
          )
        : convertedCompressions,
    [convertedCompressions, selectedMaterialName]
  );

  const handleSelectAll = () => {
    if (!selectableCompressions.length) {
      return;
    }

    const allSelected = selectableCompressions.every((item) =>
      checkedIds.includes(item.id)
    );

    if (allSelected) {
      setCheckedIds([]);
      setCheckedItems({});
    } else {
      setCheckedIds(selectableCompressions.map((item) => item.id));
      setCheckedItems(
        selectableCompressions.reduce((acc, item) => {
          acc[item.id] = true;
          return acc;
        }, {} as Record<string, boolean>)
      );
    }
  };

  const convertedStorageRows: StorageShipmentRow[] = useMemo(
    () =>
      storageProcessingObjects.map((item) => ({
        processingObjectId: item.processingObjectId,
        datetime: item.datetime
          ? getFormatDateWithDeviceTimezone(item.datetime)
          : '-',
        user: item.user || '-',
        materialName: item.materialName || '-',
        weight: `${normalizeNumber({ value: item.weight })}`,
        localStorage: item.localStorage || null,
      })),
    [storageProcessingObjects]
  );

  const storageMaterialNameOptions: Option[] = useMemo(() => {
    const seen = new Set<string>();
    originalStorageMaterial?.forEach((item) => {
      if (item.materialName) seen.add(item.materialName);
    });
    return Array.from(seen).map((name) => ({ value: name, label: name }));
  }, [originalStorageMaterial]);

  const selectedStorageMaterialName = useMemo(() => {
    const checkedRow = convertedStorageRows.find((row) =>
      checkedIds.includes(row.processingObjectId)
    );
    return checkedRow?.materialName ?? null;
  }, [checkedIds, convertedStorageRows]);

  const selectableStorageRows = useMemo(
    () =>
      selectedStorageMaterialName
        ? convertedStorageRows.filter(
            (row) => row.materialName === selectedStorageMaterialName
          )
        : convertedStorageRows,
    [convertedStorageRows, selectedStorageMaterialName]
  );

  const handleSelectAllStorage = () => {
    if (!selectableStorageRows.length) {
      return;
    }

    const allSelected = selectableStorageRows.every((item) =>
      checkedIds.includes(item.processingObjectId)
    );

    if (allSelected) {
      setCheckedIds([]);
      setCheckedItems({});
    } else {
      setCheckedIds(
        selectableStorageRows.map((item) => item.processingObjectId)
      );
      setCheckedItems(
        selectableStorageRows.reduce((acc, item) => {
          acc[item.processingObjectId] = true;
          return acc;
        }, {} as Record<string, boolean>)
      );
    }
  };

  const handleCheckItem = (id: string) => {
    if (checkedIds.includes(id)) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
      setCheckedItems({
        ...checkedItems,
        [id]: false,
      });
    } else {
      setCheckedIds([...checkedIds, id]);
      setCheckedItems({
        ...checkedItems,
        [id]: true,
      });
    }
  };

  const processingColumns = useProcessingColumns({
    checkedIds,
    convertedCompressions,
    filter,
    materialNameOptions,
    gradeOptions,
    typeOptions,
    selectedMaterialName,
    onSelectAll: handleSelectAll,
    onCheckItem: handleCheckItem,
    onFilterChange: (value) => setFilter(value),
  });

  const storageColumns = useStorageColumns({
    checkedIds,
    convertedStorageRows,
    filter,
    storageMaterialNameOptions,
    selectedMaterialName: selectedStorageMaterialName,
    onSelectAll: handleSelectAllStorage,
    onCheckItem: handleCheckItem,
    onFilterChange: (value) => setFilter(value),
  });

  const totalCheckedWeight = useMemo(() => {
    if (!checkedIds.length) return 0;

    if (isStorage) {
      return convertedStorageRows.reduce((total, row) => {
        if (checkedIds.includes(row.processingObjectId)) {
          return total.plus(new BigNumber(row.weight.replace(/,/g, '') || 0));
        }

        return total;
      }, new BigNumber(0));
    }

    return convertedCompressions.reduce((total, row) => {
      if (checkedIds.includes(row.id)) {
        console.log(row.weight);

        return total.plus(new BigNumber(row.weight.replace(/,/g, '') || 0));
      }

      return total;
    }, new BigNumber(0));
  }, [checkedIds, convertedCompressions, convertedStorageRows, isStorage]);

  const renderQRScanDataItem = (
    data: CompressionDeliveryQRSScannedData,
    isChecked: boolean,
    onToggle: (itemId: string) => void
  ) => {
    return (
      <div key={data.id} className={styles.scannedListItemContainer}>
        <div
          className={styles.scannedListItems}
          onClick={() => {
            onToggle(data.id);
            if (checkedQRIds.includes(data.id)) {
              setCheckedQRIds(
                checkedQRIds.filter((checkedId) => checkedId !== data.id)
              );
              setQRSelectedCompressions(
                QRSelectedCompressions.filter(
                  (item) => item.compressionId !== data.id
                )
              );
            } else {
              setCheckedQRIds([...checkedQRIds, data.id]);
              handleSetQRSelectedCompressions(data);
            }
          }}
          style={{
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex' }}>
            <div className={styles.iconContainer}>
              {isChecked ? (
                <div className={styles.iconCheck}>
                  <IconCheck size={20} />
                </div>
              ) : (
                <IconEyeSlash size={26} />
              )}
            </div>
            <div className={styles.materialName}>
              <span className={styles.label}>
                <Popover
                  autoGen={{
                    lines: 2,
                    odd: 4,
                  }}
                  positions={['top']}
                  containerStyle={{
                    zIndex: '300001',
                  }}
                >
                  <>{data.materialName}</>
                </Popover>
              </span>
              <span className={styles.value}>
                <Popover
                  autoGen={{
                    lines: 2,
                    odd: 4,
                  }}
                  positions={['top']}
                  containerStyle={{
                    zIndex: '300001',
                  }}
                >
                  <>{data.id}</>
                </Popover>
              </span>
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            {!isStorage && (
              <>
                <div className={styles.compressionValues}>
                  <span className={styles.compressionLabel}>
                    {t('common.grade')}
                  </span>
                  <span className={styles.compressionValue}>
                    {data.grade || '-'}
                  </span>
                </div>
                <div className={styles.compressionValues}>
                  <span className={styles.compressionLabel}>
                    {t('common.kind')}
                  </span>
                  <span className={styles.compressionValue}>
                    {data.type || '-'}
                  </span>
                </div>
              </>
            )}
            <div className={styles.compressionValues}>
              <span className={styles.compressionLabel}>
                {t('common.weight')}
              </span>
              <span className={styles.compressionValue}>
                {normalizeNumber({ value: data.weight })}kg
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBottomQRGroupButton = useCallback(
    ({
      isDisabled,
      submit: qrSubmit,
    }: {
      isDisabled: boolean;
      submit: (isCompleted?: boolean) => void;
    }) => {
      return (
        <div className={styles.actionButtonGroupQR}>
          {!isStorage && (
            <button
              id={'correct-button-qr'}
              className={styles.correctButton}
              disabled={isDisabled}
              onClick={() => setIsEditModalOpen(true)}
            >
              {t('compressions.shipment.shipment_registration.correct_button')}
            </button>
          )}

          <button
            id={'deliver-button-qr'}
            className={styles.deliverButton}
            disabled={isDisabled}
            onClick={() => qrSubmit()}
          >
            {t('compressions.shipment.shipment_registration.deliver_button')}
          </button>
        </div>
      );
    },
    [t, isStorage]
  );

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.filterForMobile}>
          <TableFilterCalendar
            label={
              t(
                isStorage
                  ? 'compressions.shipment.shipment_registration.columns.storage_datetime'
                  : 'compressions.shipment.shipment_registration.columns.processing_datetime'
              ) || ''
            }
            value={filter?.datetime}
            onChange={(value) => {
              setFilter((prev: ShipmentRegistrationFilter) => ({
                ...prev,
                datetime: value,
              }));
            }}
            expanded
            portalPositionPoint={(ref) => ref}
            endDateRequired
          />
          <TableFilterMultiSelect
            label={
              t(
                'compressions.shipment.shipment_registration.columns.material_name'
              ) || ''
            }
            options={
              isStorage ? storageMaterialNameOptions : materialNameOptions
            }
            values={filter?.materialName}
            onChange={(value) => {
              setFilter((prev: ShipmentRegistrationFilter) => ({
                ...prev,
                materialName: value,
              }));
            }}
            fullWidth
            expanded
            portalPositionPoint={(ref) => ref}
          />

          {!isStorage && (
            <>
              <TableFilterMultiSelect
                label={
                  t(
                    'compressions.shipment.shipment_registration.columns.grade'
                  ) || ''
                }
                options={gradeOptions}
                values={filter?.grade}
                onChange={(value) =>
                  setFilter((prev: ShipmentRegistrationFilter) => ({
                    ...prev,
                    grade: value,
                  }))
                }
                fullWidth
                expanded
                portalPositionPoint={(ref) => ref}
              />
              <TableFilterMultiSelect
                label={
                  t(
                    'compressions.shipment.shipment_registration.columns.kind'
                  ) || ''
                }
                options={typeOptions}
                values={filter?.kind}
                onChange={(value) =>
                  setFilter((prev: ShipmentRegistrationFilter) => ({
                    ...prev,
                    kind: value,
                  }))
                }
                fullWidth
                expanded
                portalPositionPoint={(ref) => ref}
              />
            </>
          )}
        </div>

        <div className={styles.tableContainer}>
          {isStorage ? (
            <ResponsiveHistoryTable<StorageShipmentRow>
              data={convertedStorageRows}
              columns={storageColumns}
              className={styles.historyTable}
              scrollResetDependencies={[filter, convertedStorageRows]}
              isInheritHeight
            />
          ) : (
            <ResponsiveHistoryTable<ShipmentRegistrationRow>
              data={convertedCompressions}
              columns={processingColumns}
              className={styles.historyTable}
              scrollResetDependencies={[filter, convertedCompressions]}
              isInheritHeight
            />
          )}
        </div>
      </div>
      <footer className={styles.footer}>
        <div className={styles.selectedCount}>
          {t('compressions.shipment.shipment_registration.selected', {
            totalCount: normalizeNumber({ value: checkedIds.length }),
            totalWeight: normalizeNumber({ value: totalCheckedWeight }),
          })}
        </div>

        <div className={styles.footerContent}>
          <button
            className={styles.QRButton}
            onClick={() => setModalScanQr(true)}
          >
            {t('compressions.shipment.shipment_registration.QR_button')}
          </button>
          <div className={styles.actionButtonGroup}>
            {!isStorage && (
              <button
                className={styles.correctButton}
                disabled={checkedIds.length === 0}
                onClick={() => setIsEditModalOpen(true)}
              >
                {t(
                  'compressions.shipment.shipment_registration.correct_button'
                )}
              </button>
            )}
            <button
              className={styles.deliverButton}
              disabled={checkedIds.length === 0}
              onClick={() => {
                const selectedItems = isStorage
                  ? storageProcessingObjects
                      .filter((x) => checkedIds.includes(x.processingObjectId))
                      .map((x) => ({
                        id: x.processingObjectId,
                        description: x.materialName,
                      }))
                  : compressions
                      .filter((x) => checkedIds.includes(x.compressionId))
                      .map((x) => ({
                        id: x.compressionId,
                        description: x.description,
                      }));

                return submit(selectedItems);
              }}
            >
              {t('compressions.shipment.shipment_registration.deliver_button')}
            </button>
          </div>
        </div>
      </footer>

      {modalScanQr && (
        <Portal>
          <QRScanningModal<CompressionDeliveryQRSScannedData, ScanGroupItem>
            getItemId={(data) => data.id}
            getLatestScanDisplay={(data) => {
              return {
                label: t('common.material_name') ?? '',
                name: data.materialName,
                value: normalizeNumber({ value: data.weight }),
                unit: 'KG',
              };
            }}
            renderGroupItem={({ item, isChecked, onToggle }) => {
              return renderQRScanDataItem(
                item as unknown as CompressionDeliveryQRSScannedData,
                isChecked,
                onToggle
              );
            }}
            toGroupItem={(data) => {
              return {
                ...data,
                kind: data.type ?? '-',
                grade: data.grade?.name ?? '-',
              };
            }}
            onClose={() => {
              handleClearWhenCloseQRModal();
            }}
            onScan={handleScan}
            onSubmit={({ checkedItemIds, groupedItems, onSuccess }) => {
              // Store callback and remaining count for use after delivery API completes
              qrOnSuccessRef.current = onSuccess;
              const totalItems = groupedItems.reduce(
                (sum, g) => sum + g.data.length,
                0
              );
              qrRemainingRef.current = totalItems - checkedItemIds.length;
              isFromQRRef.current = true;

              // Build compressions list from checked items
              const compressions = checkedItemIds.reduce(
                (rs: { id: string; description: string }[], key) => {
                  const matchingQRItem = groupedItems.find(
                    (item) => item.id === key
                  );
                  if (matchingQRItem) {
                    const { id, materialName } = matchingQRItem.data[0];
                    rs.push({ id, description: materialName });
                  }
                  return rs;
                },
                []
              );

              submit(compressions, true);
            }}
            renderBottomActions={renderBottomQRGroupButton}
            paused={isEditModalOpen}
            renderSelectedCount={({ countEl, checkedItems }) => {
              const totalWeight = checkedItems.reduce((total, item) => {
                return total.plus(
                  new BigNumber(item.weight.replace(/,/g, '') || 0)
                );
              }, new BigNumber(0));

              return (
                <div className={styles.selectedCountQR}>
                  {countEl}
                  <div>|</div>
                  <div>
                    <span>{t('common.total')}: </span>
                    <span style={{ color: 'white' }}>
                      {normalizeNumber({
                        value: totalWeight,
                      })}
                    </span>
                    kg
                  </div>
                </div>
              );
            }}
          />
        </Portal>
      )}

      {isEditModalOpen && !isStorage && (
        <Portal>
          <CompressionEditModal
            isOpen={isEditModalOpen}
            closeEditModal={() => setIsEditModalOpen(false)}
            afterSubmit={() => {
              callGetCompressionsApi();
              handleClearWhenCloseQRModal();
            }}
            compressionData={selectedCompressions}
          />
        </Portal>
      )}

      {modalExpected && (
        <Portal>
          <ExpectedDestinationModal
            title={t('compressions.expected_title')}
            onClose={() => setModalExpected(false)}
            options={options}
            onSubmit={(data) =>
              handleSubmit(
                data,
                typeof modalExpected === 'object'
                  ? modalExpected.compressionIds
                  : undefined
              )
            }
            submitButtonLabel={t('compressions.submit_expected')}
          />
        </Portal>
      )}

      {modalError && (
        <Portal>
          <ErrorModal
            onClose={() => setModalError(false)}
            content={t('compressions.select_same_material')}
          />
        </Portal>
      )}

      {modalNoData && (
        <Portal>
          <ErrorModal
            onClose={() => setModalNoData(false)}
            content={t('compressions.noData')}
          />
        </Portal>
      )}
    </div>
  );
};

export default ShipmentRegistration;
